import { useEffect, useRef, useState } from "react";
import { FaPlus, FaSearch } from "react-icons/fa";

const SELF_OPTION = { Doctor_Name: "Self", Doctor_Id: "60709741" };

const DoctorSearchInput = ({ value, onChange, onBlur, onSearch, onAddDoctor, onInputChange, options = [], placeholder = "Search Doctor" }) => {
    const [inputText, setInputText] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const isSearchingRef = useRef(false);

    useEffect(() => {
        setInputText(value?.Doctor_Name || "");
    }, [value]);

    useEffect(() => {
        if (isSearchingRef.current) {
            setIsOpen(true);
            setHighlightedIndex(-1);
        }
    }, [options]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const item = listRef.current.children[highlightedIndex];
            if (item) item.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex]);

    const handleInputChange = (e) => {
        const text = e.target.value;
        setInputText(text);
        setHighlightedIndex(-1);
        if (onInputChange) onInputChange(text);
        if (value) onChange(null);
    };

    const triggerSearch = () => {
        const trimmed = inputText?.trim();
        if (!trimmed || trimmed.length < 3) {
            isSearchingRef.current = false;
            setIsOpen(false);
            return;
        }
        isSearchingRef.current = true;
        onSearch(trimmed);
    };

    // "Self" pinned first, then real results
    const displayOptions = [SELF_OPTION, ...options];

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (isOpen && displayOptions.length > 0 && highlightedIndex >= 0) {
                handleSelect(displayOptions[highlightedIndex]);
            } else {
                triggerSearch();
            }
            return;
        }

        if (!isOpen) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev < displayOptions.length - 1 ? prev + 1 : 0));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : displayOptions.length - 1));
        } else if (e.key === "Escape") {
            setIsOpen(false);
            setHighlightedIndex(-1);
        }
    };

    const handleSelect = (item) => {
        setInputText(item.Doctor_Name);
        if (onInputChange) onInputChange(item.Doctor_Name);
        onChange(item);
        isSearchingRef.current = false;
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    const handleFocus = () => {
        setIsOpen(true);
    };

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <div className="doc-search-box" style={{ position: "relative", width: "100%" }}>
                <input
                    type="text"
                    className="form-control"
                    placeholder={placeholder}
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={onBlur}
                    autoComplete="off"
                    style={{
                        paddingRight: "35px",
                        borderRadius: "5px",
                        border: "1px solid #ddd",
                    }}
                />
                <button
                    type="button"
                    className="btn custom-sumbitbtn d-flex align-items-center justify-content-center"
                    style={{
                        padding: "5px",
                        position: "absolute",
                        right: "0px",
                        height: "35px",
                        width: "45px",
                        top: "50%",
                        borderTopLeftRadius: "0",
                        borderBottomLeftRadius: "0",
                        transform: "translateY(-50%)",
                    }}
                    onMouseDown={(e) => { e.preventDefault(); triggerSearch(); }}
                    title="Search"
                >
                    <FaSearch />
                </button>
            </div>

            {isOpen && (
                <ul
                    ref={listRef}
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        backgroundColor: "#fff",
                        border: "1px solid #ced4da",
                        borderRadius: "0 0 4px 4px",
                        maxHeight: "220px",
                        overflowY: "auto",
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                    }}
                >
                    <li
                        key="self-option"
                        onMouseDown={() => handleSelect(SELF_OPTION)}
                        onMouseEnter={() => setHighlightedIndex(0)}
                        onMouseLeave={() => setHighlightedIndex(-1)}
                        style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: 600,
                            borderBottom: "1px solid #f0f0f0",
                            backgroundColor: highlightedIndex === 0 ? "#c8e0ff" : "#fafafa"
                        }}
                    >
                        Self
                    </li>

                    {options.length === 0 ? (
                        <li style={{ padding: "6px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ color: "#888", fontSize: "13px" }}>
                                No doctors found{inputText ? ` for "${inputText}"` : ""}
                            </span>
                            {onAddDoctor && (
                                <button
                                    type="button"
                                    className="btn custom-sumbitbtn d-flex align-items-center justify-content-center"
                                    style={{ padding: "6px 8px", fontSize: "14px", fontWeight: "bold" }}
                                    onMouseDown={(e) => { e.preventDefault(); onAddDoctor(inputText); }}
                                    title="Add New Doctor"
                                >
                                    <FaPlus size={12} />
                                </button>
                            )}
                        </li>
                    ) : (
                        options.map((item, index) => (
                            <li
                                key={item.Doctor_Id}
                                onMouseDown={() => handleSelect(item)}
                                onMouseEnter={() => setHighlightedIndex(index + 1)}
                                onMouseLeave={() => setHighlightedIndex(-1)}
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    borderBottom: "1px solid #f0f0f0",
                                    backgroundColor: highlightedIndex === index + 1 ? "#c8e0ff" : "#fff"
                                }}
                            >
                                Dr. {item.Doctor_Name}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
};

export default DoctorSearchInput;